/**
 * Modelo Page - Model Library Dashboard
 * Shows all translation models, their specs, and testing performance
 * Password protected (browser can save password)
 */

import { useState, useEffect } from 'react';
import './ModeloPage.css';

// Simple password - browser will offer to save it
const MODELO_PASSWORD = 'translayshun2026';

interface Model {
  id: string;
  name: string;
  size: string;
  params: string;
  type: 'ollama' | 'huggingface';
  languages: string[];
  specialties: string[];
  useCase: string;
  status: 'ready' | 'downloading' | 'pending' | 'testing';
  performance?: {
    bleuScore?: number;
    latencyMs?: number;
    tokensPerSec?: number;
  };
}

const MODEL_LIBRARY: Model[] = [
  // Ollama Models
  {
    id: 'phi3-3.8b',
    name: 'Phi-3 3.8B',
    size: '2.2 GB',
    params: '3.8B',
    type: 'ollama',
    languages: ['Multi'],
    specialties: ['Understanding', 'Reasoning'],
    useCase: 'Understanding layer - tone/context detection',
    status: 'ready',
  },
  {
    id: 'mistral-7b',
    name: 'Mistral 7B Instruct',
    size: '4.4 GB',
    params: '7B',
    type: 'ollama',
    languages: ['Multi'],
    specialties: ['Instruction Following', 'Style'],
    useCase: 'Style transfer - audience adaptation',
    status: 'ready',
  },
  {
    id: 'deepseek-r1-7b',
    name: 'DeepSeek-R1 7B',
    size: '4.7 GB',
    params: '7B',
    type: 'ollama',
    languages: ['Multi', 'Chinese'],
    specialties: ['Reasoning', 'Understanding'],
    useCase: 'Understanding layer alternative',
    status: 'ready',
  },
  {
    id: 'deepseek-r1-14b',
    name: 'DeepSeek-R1 14B',
    size: '9.0 GB',
    params: '14B',
    type: 'ollama',
    languages: ['Multi', 'Chinese'],
    specialties: ['Reasoning', 'Style'],
    useCase: 'Style transfer alternative',
    status: 'ready',
  },
  {
    id: 'qwen3-8b',
    name: 'Qwen3 8B',
    size: '5.2 GB',
    params: '8B',
    type: 'ollama',
    languages: ['Multi', 'Chinese', 'English'],
    specialties: ['Multilingual', 'Reasoning'],
    useCase: 'General multilingual, strong Chinese',
    status: 'ready',
  },
  {
    id: 'gemma3-4b',
    name: 'Gemma 3 4B',
    size: '3.3 GB',
    params: '4B',
    type: 'ollama',
    languages: ['Multi'],
    specialties: ['Lightweight', 'Fast'],
    useCase: 'Lightweight general purpose',
    status: 'ready',
  },
  {
    id: 'dictalm-4b',
    name: 'DictaLM 2.0 Instruct',
    size: '4.1 GB',
    params: '4B',
    type: 'ollama',
    languages: ['Hebrew', 'English'],
    specialties: ['Hebrew', 'Israeli Context'],
    useCase: 'Hebrew validation layer',
    status: 'ready',
  },
  // HuggingFace Models
  {
    id: 'gigachat3-10b',
    name: 'GigaChat3 10B-A1.8B',
    size: '~6 GB',
    params: '10B MoE (1.8B active)',
    type: 'huggingface',
    languages: ['Russian'],
    specialties: ['Russian', 'Slavic'],
    useCase: 'Russian language specialist',
    status: 'downloading',
  },
  {
    id: 'eurollm-1.7b',
    name: 'EuroLLM 1.7B Instruct',
    size: '~3 GB',
    params: '1.7B',
    type: 'huggingface',
    languages: ['35 EU Languages', 'Russian', 'Ukrainian'],
    specialties: ['European', 'EU Compliance'],
    useCase: 'European language polish',
    status: 'downloading',
  },
  {
    id: 'babel-9b',
    name: 'Babel 9B Chat',
    size: '~18 GB',
    params: '9B',
    type: 'huggingface',
    languages: ['25 Languages', 'Russian', 'Arabic', 'Chinese', 'Hindi'],
    specialties: ['Multilingual', 'Global Coverage'],
    useCase: 'Global language coverage (90% speakers)',
    status: 'downloading',
  },
  {
    id: 'minilingua-1b',
    name: 'MiniLingua 1B Instruct',
    size: '~2 GB',
    params: '1B',
    type: 'huggingface',
    languages: ['14 EU Languages'],
    specialties: ['European', 'Compact'],
    useCase: 'Lightweight European polish',
    status: 'downloading',
  },
  {
    id: 'openeurollm-9b',
    name: 'OpenEuroLLM 9B',
    size: '~18 GB',
    params: '9B',
    type: 'huggingface',
    languages: ['EU Languages'],
    specialties: ['European', 'Open Source'],
    useCase: 'EU consortium model',
    status: 'downloading',
  },
  {
    id: 'tildeopen-30b',
    name: 'TildeOpen 30B',
    size: '~61 GB',
    params: '30B',
    type: 'huggingface',
    languages: ['32 Languages', 'Russian', 'Ukrainian', 'Balto-Slavic'],
    specialties: ['Slavic', 'Baltic', 'European'],
    useCase: 'Balto-Slavic specialist (optional)',
    status: 'pending',
  },
  // TranslateGemma - Google's translation-specific models
  {
    id: 'translategemma-4b',
    name: 'TranslateGemma 4B',
    size: '~8 GB',
    params: '4B',
    type: 'huggingface',
    languages: ['55 Languages'],
    specialties: ['Translation', 'Google', 'Gemma 3'],
    useCase: 'Primary translation model - fast inference',
    status: 'pending',
  },
  {
    id: 'translategemma-12b',
    name: 'TranslateGemma 12B',
    size: '~24 GB',
    params: '12B',
    type: 'huggingface',
    languages: ['55 Languages'],
    specialties: ['Translation', 'Google', 'Gemma 3', 'High Quality'],
    useCase: 'Primary translation model - highest quality',
    status: 'pending',
  },
  // Qwen3-TTS - Text-to-Speech
  {
    id: 'qwen3-tts-0.6b',
    name: 'Qwen3-TTS 0.6B',
    size: '~1.2 GB',
    params: '0.6B',
    type: 'huggingface',
    languages: ['10 Languages', 'Chinese', 'English'],
    specialties: ['TTS', 'Voice', '17 Voices', 'Real-time'],
    useCase: 'Text-to-Speech output (97ms latency)',
    status: 'pending',
  },
];

export function ModeloPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'ollama' | 'huggingface'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Check if already authenticated
  useEffect(() => {
    const saved = localStorage.getItem('modelo_auth');
    if (saved === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === MODELO_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('modelo_auth', 'true');
      setError('');
    } else {
      setError('Incorrect password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('modelo_auth');
  };

  const filteredModels = MODEL_LIBRARY.filter((model) => {
    const matchesFilter = filter === 'all' || model.type === filter;
    const matchesSearch =
      searchTerm === '' ||
      model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.languages.some((l) => l.toLowerCase().includes(searchTerm.toLowerCase())) ||
      model.specialties.some((s) => s.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const getStatusBadge = (status: Model['status']) => {
    const colors = {
      ready: '#22c55e',
      downloading: '#f59e0b',
      pending: '#6b7280',
      testing: '#3b82f6',
    };
    return (
      <span className="status-badge" style={{ backgroundColor: colors[status] }}>
        {status}
      </span>
    );
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="modelo-login">
        <div className="login-card">
          <h1>Modelo Dashboard</h1>
          <p>Model Library Access</p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              autoComplete="current-password"
              name="modelo-password"
            />
            <button type="submit">Access Dashboard</button>
            {error && <p className="error">{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="modelo-page">
      <header className="modelo-header">
        <div className="header-left">
          <h1>Modelo Dashboard</h1>
          <p>Translation Model Library</p>
        </div>
        <div className="header-right">
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>

      <div className="modelo-stats">
        <div className="stat-card">
          <h3>{MODEL_LIBRARY.length}</h3>
          <p>Total Models</p>
        </div>
        <div className="stat-card">
          <h3>{MODEL_LIBRARY.filter((m) => m.status === 'ready').length}</h3>
          <p>Ready</p>
        </div>
        <div className="stat-card">
          <h3>{MODEL_LIBRARY.filter((m) => m.status === 'downloading').length}</h3>
          <p>Downloading</p>
        </div>
        <div className="stat-card">
          <h3>{MODEL_LIBRARY.filter((m) => m.type === 'ollama').length}</h3>
          <p>Ollama</p>
        </div>
        <div className="stat-card">
          <h3>{MODEL_LIBRARY.filter((m) => m.type === 'huggingface').length}</h3>
          <p>HuggingFace</p>
        </div>
      </div>

      <div className="modelo-filters">
        <input
          type="text"
          placeholder="Search models, languages, specialties..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="filter-buttons">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={filter === 'ollama' ? 'active' : ''}
            onClick={() => setFilter('ollama')}
          >
            Ollama
          </button>
          <button
            className={filter === 'huggingface' ? 'active' : ''}
            onClick={() => setFilter('huggingface')}
          >
            HuggingFace
          </button>
        </div>
      </div>

      <div className="modelo-grid">
        {filteredModels.map((model) => (
          <div key={model.id} className={`model-card ${model.status}`}>
            <div className="model-header">
              <h3>{model.name}</h3>
              {getStatusBadge(model.status)}
            </div>
            <div className="model-meta">
              <span className="type-badge">{model.type}</span>
              <span className="size">{model.size}</span>
              <span className="params">{model.params}</span>
            </div>
            <p className="use-case">{model.useCase}</p>
            <div className="model-languages">
              <strong>Languages:</strong>
              <div className="tags">
                {model.languages.map((lang) => (
                  <span key={lang} className="tag">
                    {lang}
                  </span>
                ))}
              </div>
            </div>
            <div className="model-specialties">
              <strong>Specialties:</strong>
              <div className="tags">
                {model.specialties.map((spec) => (
                  <span key={spec} className="tag specialty">
                    {spec}
                  </span>
                ))}
              </div>
            </div>
            {model.performance && (
              <div className="model-performance">
                <strong>Performance:</strong>
                <div className="perf-metrics">
                  {model.performance.bleuScore && (
                    <span>BLEU: {model.performance.bleuScore}</span>
                  )}
                  {model.performance.latencyMs && (
                    <span>Latency: {model.performance.latencyMs}ms</span>
                  )}
                  {model.performance.tokensPerSec && (
                    <span>{model.performance.tokensPerSec} tok/s</span>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <footer className="modelo-footer">
        <p>Translayshun Model Library - {new Date().toLocaleDateString()}</p>
      </footer>
    </div>
  );
}
